import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Table, Button, Input, Select, Tag, Space, Modal, Form,
  message, Popconfirm, Card, Row, Col, AutoComplete, Tooltip, Badge, Grid
} from 'antd'
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, EyeInvisibleOutlined, TeamOutlined, FilterOutlined,
  AppstoreOutlined
} from '@ant-design/icons'
import {
  getAccounts, createAccount, updateAccount, deleteAccount,
  type Account
} from '../../../../api/operations/accounts'
import { getUsers, type User } from '../../../../api/operations/users'
import { useAuthStore } from '../../../../store/auth'

const { Option } = Select

const PLATFORM_MAP: Record<string, string> = {
  FACEBOOK: 'FB',
  INSTAGRAM: 'INS',
  TIKTOK: 'TK',
  YOUTUBE: 'YTB',
  WECHAT_CHANNEL: '微信视频号',
}

const PLATFORM_STYLE: Record<string, { bg: string; color: string; border: string }> = {
  FACEBOOK:  { bg: '#e6f4ff', color: '#1677ff', border: '#91caff' },
  INSTAGRAM: { bg: '#fff0f6', color: '#c41d7f', border: '#ffadd2' },
  TIKTOK:    { bg: '#f6ffed', color: '#389e0d', border: '#b7eb8f' },
  YOUTUBE:   { bg: '#fff2f0', color: '#cf1322', border: '#ffa39e' },
  WECHAT_CHANNEL: { bg: '#f0fff4', color: '#07c160', border: '#95eeba' },
}

const PLATFORM_ORDER: Record<string, number> = {
  FACEBOOK: 1,
  INSTAGRAM: 2,
  TIKTOK: 3,
  YOUTUBE: 4,
  WECHAT_CHANNEL: 5,
}

const CUSTOM_GROUP_ORDER: Record<string, number> = {
  'Danny Robin': 1,
  'Danny 1': 2,
  'Danny 2': 3,
  'Robin 1': 4,
  'Robin 2': 5,
}

const STATUS_MAP: Record<string, { text: string; color: string; dot: string }> = {
  ACTIVE:   { text: '正常运营', color: 'success', dot: '#52c41a' },
  PAUSED:   { text: '暂未运营', color: 'warning', dot: '#faad14' },
  BANNED:   { text: '已封禁',   color: 'error',   dot: '#f5222d' },
  APPEALING:{ text: '申诉中',   color: 'processing', dot: '#1890ff' },
}

const GROUP_CARD_BGS = [
  { body: '#f0f9ff', head: '#e0f2fe', border: '#bae6fd' },
  { body: '#f0fdf4', head: '#dcfce7', border: '#bbf7d0' },
  { body: '#fffbeb', head: '#fef3c7', border: '#fde68a' },
  { body: '#fdf2f8', head: '#fce7f3', border: '#fbcfe8' },
  { body: '#faf5ff', head: '#f3e8ff', border: '#e9d5ff' },
  { body: '#fff1f2', head: '#ffe4e6', border: '#fecdd3' },
]

const GROUP_BY_OPTIONS = [
  { value: '', label: '不分组' },
  { value: 'customGroup', label: '按自定义分组' },
  { value: 'name', label: '按账号名称' },
  { value: 'platform', label: '按平台' },
  { value: 'operators', label: '按运营人' },
]

export default function AccountListPage() {
  const [accounts, setAccounts] = useState<Account[]>([])
  const [usersList, setUsersList] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingAccount, setEditingAccount] = useState<Account | null>(null)
  const [searchText, setSearchText] = useState('')
  const [filterPlatform, setFilterPlatform] = useState<string>()
  const [filterStatus, setFilterStatus] = useState<string>()
  const [groupBy, setGroupBy] = useState<string>('')
  const [form] = Form.useForm()
  const userRole = useAuthStore((s) => s.user?.role)
  const isAdmin = userRole === 'SUPER_ADMIN' || userRole === 'MANAGER'
  // 运营可新建/编辑账号，但删除/交接仍仅管理员
  const canEdit = isAdmin || userRole === 'OPERATOR'
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md

  const fetchAccounts = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getAccounts({
        search: searchText || undefined,
        platform: filterPlatform,
        status: filterStatus,
      })
      setAccounts(data)
    } finally {
      setLoading(false)
    }
  }, [searchText, filterPlatform, filterStatus])

  useEffect(() => {
    fetchAccounts()
    getUsers().then(setUsersList).catch(() => {})
  }, [fetchAccounts])

  const customGroupOptions = useMemo(() => {
    const groups = new Set<string>()
    accounts.forEach((a) => {
      if (a.customGroup) groups.add(a.customGroup)
    })
    return Array.from(groups).map((g) => ({ value: g, label: g }))
  }, [accounts])

  const stats = useMemo(() => {
    const total = accounts.length
    const active = accounts.filter((a) => a.status === 'ACTIVE').length
    const paused = accounts.filter((a) => a.status === 'PAUSED').length
    const banned = accounts.filter((a) => a.status === 'BANNED').length
    return { total, active, paused, banned }
  }, [accounts])

  const groupedData = useMemo(() => {
    if (!groupBy) return null
    const groups: Record<string, { label: string; accounts: Account[] }> = {}
    accounts.forEach((account) => {
      let key: string
      let label: string
      if (groupBy === 'customGroup') {
        key = account.customGroup || '未分组'
        label = account.customGroup || '未分组'
      } else if (groupBy === 'name') {
        key = account.name || '未命名'
        label = account.name || '未命名'
      } else if (groupBy === 'platform') {
        key = account.platform
        label = PLATFORM_MAP[account.platform] || account.platform
      } else if (groupBy === 'operators') {
        const firstOp = account.operators[0]
        key = firstOp?.id || '无负责人'
        label = firstOp?.realName || '无负责人'
      } else {
        key = '全部'
        label = '全部'
      }
      if (!groups[key]) {
        groups[key] = { label, accounts: [] }
      }
      groups[key].accounts.push(account)
    })

    Object.values(groups).forEach((g) => {
      g.accounts.sort((a, b) => {
        const orderA = PLATFORM_ORDER[a.platform] || 99
        const orderB = PLATFORM_ORDER[b.platform] || 99
        return orderA - orderB
      })
    })

    const result = Object.entries(groups).map(([key, g]) => ({ key, ...g }))
    if (groupBy === 'customGroup') {
      result.sort((a, b) => {
        const orderA = CUSTOM_GROUP_ORDER[a.key] || 99
        const orderB = CUSTOM_GROUP_ORDER[b.key] || 99
        return orderA - orderB
      })
    }
    return result
  }, [accounts, groupBy])

  const handleSubmit = async (values: any) => {
    try {
      const payload = { ...values }
      if (editingAccount) {
        await updateAccount(editingAccount.id, payload)
        message.success('更新成功')
      } else {
        await createAccount(payload)
        message.success('创建成功')
      }
      setModalVisible(false)
      setEditingAccount(null)
      form.resetFields()
      fetchAccounts()
    } catch (err: any) {
      message.error(err.message || '操作失败')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteAccount(id)
      message.success('删除成功')
      fetchAccounts()
    } catch (err: any) {
      message.error(err.message || '删除失败')
    }
  }

  const openEdit = (account: Account) => {
    setEditingAccount(account)
    form.setFieldsValue({
      ...account,
      operatorIds: account.operators.map((o) => o.id),
    })
    setModalVisible(true)
  }

  const openCreate = () => {
    setEditingAccount(null)
    form.resetFields()
    setModalVisible(true)
  }

  const renderPlatformTag = (p: string) => {
    const style = PLATFORM_STYLE[p]
    const name = PLATFORM_MAP[p] || p
    if (!style) return <Tag>{name}</Tag>
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 10px',
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 500,
          background: style.bg,
          color: style.color,
          border: `1px solid ${style.border}`,
        }}
      >
        {name}
      </span>
    )
  }

  const renderStatusTag = (s: string) => {
    const cfg = STATUS_MAP[s] || { text: s, color: 'default', dot: '#999' }
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
        <Badge color={cfg.dot} />
        {cfg.text}
      </span>
    )
  }

  const mobileColumns = [
    {
      title: <span style={{ fontSize: 12, color: '#999' }}>#</span>,
      key: 'index',
      width: 40,
      align: 'center' as const,
      render: (_: any, __: any, idx: number) => (
        <span style={{ color: '#bfbfbf', fontSize: 12 }}>{idx + 1}</span>
      ),
    },
    {
      title: '账号名称',
      dataIndex: 'name',
      key: 'name',
      render: (v: string, record: Account) => (
        <div>
          <div style={{ fontWeight: 500, color: '#262626', whiteSpace: 'normal', wordBreak: 'break-all', lineHeight: 1.4 }}>{v}</div>
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {renderPlatformTag(record.platform)}
            {record.operators?.length > 0 && (
              <span style={{ fontSize: 12, color: '#595959' }}>{record.operators.map((o) => o.realName).join(', ')}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: renderStatusTag,
    },
    {
      title: '',
      key: 'action',
      width: 68,
      align: 'center' as const,
      render: (_: any, record: Account) => (
        <Space size={4}>
          {canEdit && (
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
              style={{ color: '#8c8c8c' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#1677ff')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#8c8c8c')}
            />
          )}
          {isAdmin && (
            <Popconfirm
              title="确认删除"
              description="删除后不可恢复，是否继续？"
              onConfirm={() => handleDelete(record.id)}
              okText="删除"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ color: '#8c8c8c' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#ff4d4f')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#8c8c8c')}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  const accountColumns = [
    {
      title: <span style={{ fontSize: 12, color: '#999' }}>#</span>,
      key: 'index',
      width: 44,
      align: 'center' as const,
      render: (_: any, __: any, idx: number) => (
        <span style={{ color: '#bfbfbf', fontSize: 12 }}>{idx + 1}</span>
      ),
    },
    {
      title: '账号名称',
      dataIndex: 'name',
      key: 'name',
      render: (v: string) => (
        <span style={{ fontWeight: 500, color: '#262626', whiteSpace: 'normal', wordBreak: 'break-all', lineHeight: 1.4 }}>{v}</span>
      ),
    },
    {
      title: '平台',
      dataIndex: 'platform',
      key: 'platform',
      width: 100,
      align: 'center' as const,
      render: renderPlatformTag,
    },
    {
      title: '账户类型',
      dataIndex: 'accountType',
      key: 'accountType',
      width: 100,
      render: (v: string) => v ? (
        <Tag bordered={false} style={{ fontSize: 12, background: '#f5f5f5', color: '#595959' }}>
          {v}
        </Tag>
      ) : (
        <span style={{ color: '#d9d9d9' }}>-</span>
      ),
    },
    {
      title: '运营人',
      dataIndex: 'operators',
      key: 'operators',
      width: 80,
      render: (ops: Account['operators']) =>
        ops?.length ? (
          <span style={{ fontSize: 13, color: '#434343' }}>
            {ops.map((o) => o.realName).join(', ')}
          </span>
        ) : (
          <span style={{ color: '#d9d9d9' }}>-</span>
        ),
    },
    {
      title: '登录邮箱',
      dataIndex: 'loginEmail',
      key: 'loginEmail',
      ellipsis: true,
      render: (v: string) => v || <span style={{ color: '#d9d9d9' }}>-</span>,
    },
    {
      title: '密码',
      dataIndex: 'loginPassword',
      key: 'loginPassword',
      width: 110,
      render: (v: string) => v ? (
        <Tooltip title={v}>
          <span
            style={{
              display: 'inline-block',
              maxWidth: 90,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: 'monospace',
              fontSize: 12,
              color: '#8c8c8c',
              background: '#f5f5f5',
              padding: '2px 8px',
              borderRadius: 4,
            }}
          >
            {v}
          </span>
        </Tooltip>
      ) : (
        <span style={{ color: '#d9d9d9' }}>-</span>
      ),
    },
    {
      title: '手机号',
      dataIndex: 'loginPhone',
      key: 'loginPhone',
      width: 110,
      render: (v: string) => v || <span style={{ color: '#d9d9d9' }}>-</span>,
    },
    {
      title: '20260205粉丝量',
      dataIndex: 'followerCount',
      key: 'followerCount',
      width: 110,
      align: 'right' as const,
      render: (v: number) => (
        <span style={{ fontWeight: 500, color: '#262626', fontSize: 13 }}>
          {v?.toLocaleString() || '0'}
        </span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: renderStatusTag,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 160,
      render: (v: string) =>
        v ? (
          <span style={{ fontSize: 12, color: '#595959', lineHeight: 1.5, whiteSpace: 'normal', wordBreak: 'break-all' }}>
            {v}
          </span>
        ) : (
          <span style={{ color: '#d9d9d9' }}>-</span>
        ),
    },
    {
      title: '',
      key: 'action',
      width: 72,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_: any, record: Account) => (
        <Space size={4}>
          {canEdit && (
            <Tooltip title="编辑">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(record)}
                style={{ color: '#8c8c8c' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#1677ff')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#8c8c8c')}
              />
            </Tooltip>
          )}
          {isAdmin && (
            <Popconfirm
              title="确认删除"
              description="删除后不可恢复，是否继续？"
              onConfirm={() => handleDelete(record.id)}
              okText="删除"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="删除">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ color: '#8c8c8c' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#ff4d4f')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#8c8c8c')}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  const tableProps = {
    columns: isMobile ? mobileColumns : accountColumns,
    loading,
    pagination: false as const,
    size: 'small' as const,
    bordered: false,
    showHeader: !isMobile,
    rowKey: 'id' as const,
    style: { background: '#fff' },
  }

  return (
    <div style={{ padding: '0 0 24px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: isMobile ? 12 : 0,
          marginBottom: 20,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 20, fontWeight: 600, color: '#1f1f1f' }}>
            账号管理
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#8c8c8c' }}>
            共 {stats.total} 个账号 ·
            <span style={{ color: '#52c41a', marginLeft: 8 }}>正常 {stats.active}</span>
            <span style={{ color: '#faad14', marginLeft: 8 }}>暂停 {stats.paused}</span>
            <span style={{ color: '#ff4d4f', marginLeft: 8 }}>封禁 {stats.banned}</span>
          </p>
        </div>
        {canEdit && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
            style={{ borderRadius: 6, height: 36 }}
            block={isMobile}
          >
            新增账号
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card
        styles={{ body: { padding: isMobile ? '12px 16px' : '14px 20px' } }}
        style={{ marginBottom: 16, borderRadius: 8, borderColor: '#f0f0f0' }}
      >
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={6} lg={5}>
            <Input
              placeholder="搜索账号名称"
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={fetchAccounts}
              style={{ width: '100%', borderRadius: 6 }}
              allowClear
            />
          </Col>
          <Col xs={12} sm={12} md={4} lg={4}>
            <Select
              placeholder="平台"
              allowClear
              style={{ width: '100%', borderRadius: 6 }}
              value={filterPlatform}
              onChange={setFilterPlatform}
              suffixIcon={<FilterOutlined style={{ color: '#bfbfbf' }} />}
            >
              {Object.entries(PLATFORM_MAP).map(([k, v]) => (
                <Option key={k} value={k}>{v}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={12} md={5} lg={4}>
            <Select
              placeholder="运营状态"
              allowClear
              style={{ width: '100%', borderRadius: 6 }}
              value={filterStatus}
              onChange={setFilterStatus}
              suffixIcon={<FilterOutlined style={{ color: '#bfbfbf' }} />}
            >
              {Object.entries(STATUS_MAP).map(([k, v]) => (
                <Option key={k} value={k}>{v.text}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={12} md={5} lg={4}>
            <Select
              placeholder="分组方式"
              style={{ width: '100%', borderRadius: 6 }}
              value={groupBy}
              onChange={setGroupBy}
              suffixIcon={<AppstoreOutlined style={{ color: '#bfbfbf' }} />}
            >
              {GROUP_BY_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={12} md={4} lg={3}>
            <Button onClick={fetchAccounts} style={{ borderRadius: 6, width: '100%' }}>
              刷新
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      {groupBy && groupedData ? (
        <div>
          {groupedData.map((group, idx) => {
            const bg = GROUP_CARD_BGS[idx % GROUP_CARD_BGS.length]
            return (
            <Card
              key={group.key}
              size="small"
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#262626' }}>
                    {group.label}
                  </span>
                  <Tag color="default" style={{ fontSize: 12, borderRadius: 10, margin: 0 }}>
                    {group.accounts.length}
                  </Tag>
                </div>
              }
              style={{
                marginBottom: 12,
                borderRadius: 8,
                borderColor: bg.border,
                background: bg.body,
              }}
              headStyle={{
                background: bg.head,
                borderBottom: `1px solid ${bg.border}`,
                padding: '10px 16px',
              }}
            >
              <Table
                {...tableProps}
                dataSource={group.accounts}
              />
            </Card>
            )})}
          {groupedData.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60, color: '#bfbfbf' }}>
              <TeamOutlined style={{ fontSize: 40, marginBottom: 12, display: 'block' }} />
              暂无账号数据
            </div>
          )}
        </div>
      ) : (
        <Card
          styles={{ body: { padding: 0 } }}
          style={{ borderRadius: 8, borderColor: '#f0f0f0', overflow: 'hidden' }}
        >
          <Table
            {...tableProps}
            dataSource={accounts}
            scroll={{ x: isMobile ? undefined : 'max-content' }}
          />
        </Card>
      )}

      {/* Modal */}
      <Modal
        title={
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {editingAccount ? '编辑账号' : '新增账号'}
          </span>
        }
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false)
          setEditingAccount(null)
          form.resetFields()
        }}
        width={isMobile ? '90%' : 720}
        okText="保存"
        cancelText="取消"
        styles={{ body: { paddingTop: 20 } }}
        okButtonProps={{ block: isMobile }}
        cancelButtonProps={{ block: isMobile }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="name" label="账号名称" rules={[{ required: true, message: '请输入账号名称' }]}>
                <Input placeholder="如：Foshan Boswindor Window and Door Limited" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="platform" label="社媒平台" rules={[{ required: true, message: '请选择社媒平台' }]}>
                <Select placeholder="选择平台">
                  {Object.entries(PLATFORM_MAP).map(([k, v]) => (
                    <Option key={k} value={k}>{v}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="accountType" label="社媒账户">
                <Input placeholder="如：主公共主页、主账户、混剪账号01" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="operatorIds" label="运营人">
                <Select mode="multiple" placeholder="选择运营人" allowClear>
                  {usersList.map((user) => (
                    <Option key={user.id} value={user.id}>{user.realName}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="customGroup" label="自定义分组">
                <AutoComplete
                  placeholder="选择已有分组或输入新分组名"
                  allowClear
                  options={customGroupOptions}
                  filterOption={(inputValue, option) =>
                    (option?.label ?? '').toLowerCase().includes(inputValue.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="status" label="运营状态" initialValue="ACTIVE">
                <Select placeholder="选择运营状态">
                  {Object.entries(STATUS_MAP).map(([k, v]) => (
                    <Option key={k} value={k}>{v.text}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="loginEmail" label="登录邮箱">
                <Input placeholder="登录邮箱" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="loginPhone" label="手机号">
                <Input placeholder="绑定的手机号" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="loginPassword" label="密码">
                <Input.Password
                  placeholder="账号登录密码"
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="followerCount" label="粉丝量">
                <Input type="number" placeholder="粉丝数量" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} placeholder="其他备注信息" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
